import { schAloftPrice } from '@aloft/toolkit-shared';
import { Text, View } from '@react-pdf/renderer';

import { COMMENTARY_TEXT, getMethodologyText } from '../../commentary/consts.ts';
import { FlexRow } from '../components/FlexRow.tsx';
import { List } from '../components/List.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles } from '../styles.ts';
import { MethodologyPageProps } from '../types.ts';

export const MethodologyPage = ({
  subjectAddress,
  propertyType,
  clientName,
  appraiserFee,
  amcFee,
}: MethodologyPageProps) => {
  const { title } = COMMENTARY_TEXT.methodology;

  const methodologyText = getMethodologyText(propertyType);

  return (
    <Tkv2PDFPage title={title} address={subjectAddress}>
      {methodologyText.map((text) => (
        <Text key={text} style={fontStyles.body}>
          {text}
        </Text>
      ))}

      {/* About This Report */}
      <Text style={fontStyles.h4}>About This Report</Text>
      <FlexRow gap={40}>
        <Text style={fontStyles.body}>
          <Text style={fontStyles.bodyBold}>Company:&nbsp;</Text>
          Appraisify
        </Text>
        <Text style={fontStyles.body}>
          <Text style={fontStyles.bodyBold}>Platform:&nbsp;</Text>
          Appraisify Toolkit
        </Text>
      </FlexRow>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>Intended User:&nbsp;</Text>
        {clientName} and affiliates for collateral underwriting, asset review, and portfolio risk.
        No other party is an intended user. Reliance by others is prohibited.
      </Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>Intended Use:&nbsp;</Text>
        To assist {clientName} in evaluating the as-is market value of the subject residential
        property, as of the effective date, for collateral underwriting and credit decisioning of a
        home equity line of credit secured by the subject.
      </Text>

      {/* Definition of Market Value */}
      <Text style={fontStyles.h4}>Definition of Market Value</Text>
      <Text style={fontStyles.body}>
        The most probable price that a property should bring in a competitive and open market under
        all conditions requisite to a fair sale with the buyer and seller each acting prudently and
        knowledgeably, and assuming the price is not affected by undue stimulus. Implicit in this
        definition is the consummation of a sale as of a specified date and the passing of title
        from seller to buyer under conditions whereby:
      </Text>

      <List
        type="bullet"
        items={[
          <Text key="buyer-seller-motivation" style={fontStyles.body}>
            Buyer and seller are typically motivated;
          </Text>,
          <Text key="well-informed-parties" style={fontStyles.body}>
            Both parties are well informed or well advised, and each acting in what he or she
            considers his or her own best interest;
          </Text>,
          <Text key="reasonable-exposure-time" style={fontStyles.body}>
            A reasonable time is allowed for exposure in the open market;
          </Text>,
          <Text key="payment-terms" style={fontStyles.body}>
            Payment is made in terms of cash in U.S. dollars or in terms of financial arrangements
            comparable thereto; and
          </Text>,
          <Text key="normal-consideration" style={fontStyles.body}>
            The price represents the normal consideration for the property sold unaffected by
            special or creative financing or sales concessions granted by anyone associated with the
            sale.
          </Text>,
        ]}
      />

      {/* Scope of Work */}
      <Text style={fontStyles.h4}>Scope of Work</Text>
      <List
        type="number"
        items={[
          <Text key="inspection" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>Inspection&nbsp;</Text>
            The appraiser did not personally inspect the subject or comparables. Inspectify, a
            non-licensed third-party vendor, performed an interior and exterior inspection of the
            subject and supplied time-stamped photos and observable facts. The appraiser reviewed
            that content for reasonableness and verified key physical characteristics from MLS and
            public records, reconciling discrepancies where found. Any data-collector opinions were
            not incorporated as appraisal opinions and the appraiser made an independent evaluation
            of the facts reported.
          </Text>,
          <Text key="approaches" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>Approaches&nbsp;</Text>
            Sales Comparison was developed as the primary method. Cost and Income approaches are not
            reported unless specifically required by engagement. The limited scope is appropriate
            for the intended use.
          </Text>,
          <Text key="feature-reconciliation" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>
              Feature reconciliation and comp selection.&nbsp;
            </Text>
            For each order the appraiser reconciled subject and comparable features between MLS and
            public record data, then used Toolkit&apos;s comp search engine to select the most
            relevant comparables. Data reliability is addressed in Assumptions and limiting
            conditions.
          </Text>,
          <View key="adjustment-development">
            <Text style={fontStyles.body}>
              <Text style={fontStyles.bodyBold}>Adjustment development.&nbsp;</Text>
              Appraisify&apos;s workflow uses a three-layer method:
            </Text>
            <List
              type="bullet"
              items={[
                <Text key="depreciated-cost" style={fontStyles.body}>
                  Depreciated Cost baseline. Each selected comparable is processed through
                  depreciated cost using Craftsman cost data for replacement cost new, local
                  multipliers, physical depreciation, and extraction of site value from each comp to
                  indicate the subject&apos;s site value. This informs adjustments and creates the
                  Paired Cost Framework. This is an internal analytic, not a reported Cost Approach.
                </Text>,
                <Text key="paired-sales-tests" style={fontStyles.body}>
                  Paired-sales tests to validate or override the baseline where paired observations
                  exist.
                </Text>,
                <Text key="sensitivity-checks" style={fontStyles.body}>
                  Sensitivity checks on key features to ensure the final grid reflects market
                  reaction rather than model outputs.
                </Text>,
                <Text key="value-opinion" style={fontStyles.body}>
                  The value opinion is the appraiser&apos;s independent conclusion.
                </Text>,
              ]}
            />
          </View>,
          <Text key="time-adjustments" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>Time adjustments.&nbsp;</Text>
            Market-level time trends are benchmarked with the Zillow Home Value Index (ZHVI) for the
            narrowest reasonable geography. See Time adjustment disclosure and extraordinary
            assumption below.
          </Text>,
        ]}
      />

      {/* Extraordinary Assumptions */}
      <Text style={fontStyles.h4}>Extraordinary Assumptions</Text>
      <Text style={fontStyles.body}>
        Use of the following extraordinary assumptions may affect assignment results:
      </Text>
      <List
        type="number"
        items={[
          <Text key="photos" style={fontStyles.body}>
            Inspectify&apos;s interior and exterior photos and observations fairly depict current
            subject characteristics and condition on the stated inspection date.
          </Text>,
          <Text key="mls-data" style={fontStyles.body}>
            MLS and public record data used for subject and comparable feature reconciliation are
            accurate in the respects adopted.
          </Text>,
          <Text key="ZHVI" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>ZHVI extraordinary assumption.&nbsp;</Text>
            ZHVI for the selected geography is a smoothed, seasonally adjusted monthly series
            designed to capture the **typical** home value from the middle tier of the local
            distribution and is a reasonable proxy for directional price movement for the
            subject&apos;s market segment over the period used. If the ZHVI trend is not
            representative of the subject&apos;s micro-market, time adjustments and the opinion of
            value could be affected.
          </Text>,
        ]}
      />

      {/* Assumptions and Limiting Conditions */}
      <Text style={fontStyles.h4}>Assumptions and Limiting Conditions</Text>
      <Text style={fontStyles.body}>
        The real property interest appraised is fee simple, marketable title assumed. The appraiser
        is not responsible for legal matters affecting title. This report is not an environmental
        assessment or a home inspection. Distribution must include the complete report with all
        statements and conditions.
      </Text>

      {/* Time Adjustment Method Using ZHVI */}
      <Text style={fontStyles.h4}>Time Adjustment Method Using ZHVI</Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>What ZHVI is.&nbsp;</Text>
        ZHVI represents the **typical** home value for a region, built from monthly changes in
        property-level Zestimates and commonly published as a smoothed, seasonally adjusted series
        that captures the middle tier of the local price distribution.
      </Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>How it&apos;s applied.&nbsp;</Text>
        The appraiser selects the narrowest relevant geography, computes the monthly rate of change
        for the purposes of applying time adjustments. ZHVI is a market-level indicator and may not
        fully capture subject-specific dynamics.
      </Text>

      {/* Third Party Inspection Disclosure */}
      <Text style={fontStyles.h4}>Third Party Inspection Disclosure</Text>
      <Text style={fontStyles.body}>
        Inspectify is a non-licensed third-party vendor engaged by the client to collect photos and
        observable facts. The vendor did not develop a value opinion and is not acting as an
        appraiser. Any opinions by the vendor are not incorporated as appraisal opinions. The
        appraiser independently evaluated the facts reported.
      </Text>

      {/* AMC Fee Disclosure */}
      <Text style={fontStyles.h4}>AMC Fee Disclosure</Text>
      <Text style={fontStyles.body}>
        For this assignment, the appraiser was paid {schAloftPrice.parse(appraiserFee).toString()}
        &nbsp;for appraisal services. Appraisify received an AMC fee of&nbsp;
        {schAloftPrice.parse(amcFee).toString()} for administrative, technology, and quality control
        services related to this order. These amounts are separate, and the appraiser&apos;s
        compensation was not contingent on the value opinion or any loan decision.
      </Text>

      {/* Data and Model Disclosures */}
      <Text style={fontStyles.h4}>Data and Model Disclosures</Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>Appraisify Toolkit.&nbsp;</Text>
        The order was originated, analyzed, and delivered through Appraisify Toolkit. Platform tools
        organize information and support the appraiser&apos;s work. The appraiser remains
        responsible for all analyses and conclusions.
      </Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>
          Craftsman cost data and Paired Cost Framework.&nbsp;
        </Text>
        Craftsman references were used to estimate replacement cost new and local multipliers and to
        derive physical depreciation and site-value extractions for selected comparables. These
        internal calculations supported adjustments and the Paired Cost Framework. A formal Cost
        Approach is not reported. Workfile retains the calculations.
      </Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>ZHVI.&nbsp;</Text>
        ZHVI is a smoothed, seasonally adjusted monthly series designed to reflect the typical home
        value for the middle tier of properties in the selected region. It is used as a directional
        indicator of market trend and reconciled to micro-market evidence.
      </Text>

      {/* Disclaimers */}
      <Text style={fontStyles.h4}>Disclaimers</Text>
      <List
        type="bullet"
        items={[
          <Text key="not-inspection" style={fontStyles.body}>
            No inspection by appraiser. Unseen conditions could affect value. This uncertainty is
            reflected in the scope and reconciliation.
          </Text>,
          <Text key="data-furnished" style={fontStyles.body}>
            Data furnished by others. Third-party and public data are assumed reliable but not
            guaranteed. The appraiser does not assume responsibility for the accuracy of items
            furnished by others.
          </Text>,
          <Text key="distribution" style={fontStyles.body}>
            Distribution. Any distribution must include the complete report with all statements and
            conditions.
          </Text>,
        ]}
      />
    </Tkv2PDFPage>
  );
};
